import ImageZoom from '../components/ImageZoom';
import Drawer from 'drawer';
import Slider from '../utils/Slider';
import loadjs from 'loadjs';
import EventHandler from '../utils/EventHandler';
import trackEvent from '../utils/trackEvent';

import '../../styles/sections/product-features.scss'

export default class ProductFeatures {
  constructor(section) {
    this.el = section.el;
    this.productFeatureSlider = [];
    this.observers = [];
    this.videos = [];
    this.ev = new EventHandler();

    this.yotpoEl = this.el.querySelector('[data-yotpo-url]');

    if (this.el.querySelector('.rimg-video')) {
      loadjs('https://embed.cloudflarestream.com/embed/sdk.latest.js', () => {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const videoObject = this.videos.find(video => video.id === entry.target.dataset.videoId)
            if (entry.isIntersecting || entry.intersectionRatio > 0) {
              videoObject.stream.play();
              if (videoObject.tracked === false) {
                videoObject.tracked = true;
                // window.dataLayer.push({ 'event': 'Played Video - Below the Fold', 'videoId': entry.target.dataset.videoId });
              }
            } else {
              videoObject.stream.pause();
            }
          });
        }, {
          rootMargin: '0px 0px 0px 0px'
        });

        this.el.querySelectorAll('.rimg-video').forEach(video => {
          if (video.dataset.src) {
            video.src = video.dataset.src;
          }
          this.videos.push({
            id: video.dataset.videoId,
            el: video,
            stream: Stream(video),
            tracked: false,
          })

          io.observe(video);
        });

        this.observers.push(io);
      });
    }

    const findSibling = (el, selector) => {
      let sibling = el.nextElementSibling;
      while (sibling) {
        if (sibling.matches(selector)) {
          return sibling;
        }
        sibling = sibling.nextElementSibling;
      }
    }

    const toggleAria = (target) => {
      if (target.classList.contains('expanded')) {
        target.setAttribute('aria-expanded', 'true')
      }
    }

    this.el.querySelectorAll('.product-feature-toggle').forEach(feature => {
      this.ev.register(feature, 'click', (e) => {
        const content = findSibling(e.currentTarget, '.product-feature-inner');
        const target = e.currentTarget;
        if (target.getAttribute('aria-expanded') === 'true') {
          target.setAttribute('aria-expanded', 'false');
          target.scrollTop;
        }
        if (content) {
          content.addEventListener('transitionend', () => toggleAria(target), { once: true });
        }
        target.classList.toggle('expanded');
      });
    });

    // Help/Questions accordion (close others when one opens)
    this.el.querySelectorAll('.product-help .help-question').forEach(question => {
      this.ev.register(question, 'click', (e) => {
        e.preventDefault();
        const questionEl = e.currentTarget;
        const isExpanded = questionEl.classList.contains('expanded');
        const helpContainer = questionEl.closest('.product-help');
        
        // Apply all class changes in one synchronous block so transitions happen simultaneously
        if (helpContainer) {
          const allQuestions = Array.from(helpContainer.querySelectorAll('.help-question'));
          
          // Remove expanded from all questions first, then add to clicked one if needed
          // This ensures all DOM updates happen in the same frame
          allQuestions.forEach(q => q.classList.remove('expanded'));
          
          if (!isExpanded) {
            questionEl.classList.add('expanded');

            // Track question open in Heap
            const questionText = questionEl.querySelector('.help-question-text');
            const handle = helpContainer.dataset.productHandle || '';
            const vendor = helpContainer.dataset.productVendor || '';
            trackEvent('OpenedProductQuestion', {
              question: questionText ? questionText.textContent.trim() : '',
              handle,
              vendor
            });
          }
        }
        
        // Handle hash navigation if needed
        if (window.location.hash === questionEl.getAttribute('href')) {
          // Already at this hash, just toggle
        } else if (!isExpanded) {
          // Update hash without scrolling (we'll handle scroll manually if needed)
          const hash = questionEl.getAttribute('href');
          if (hash) {
            history.replaceState(null, null, hash);
          }
        }
      });
    });

    if (this.el.querySelector('.product-feature-pricing_section')) {
      this.pricingPhilosophyDrawer = new Drawer(document.getElementById('drawer-pricing-philosophy'));

      this.ev.register(this.el.querySelector('.pricing-philosophy-trigger'), 'click', (e) => {
        e.preventDefault();
        this.pricingPhilosophyDrawer.open();
      });
    }

    if (this.el.querySelector('.product-feature-honest_pricing_section')) {
      this.pricingPhilosophyDrawerHp = new Drawer(document.getElementById('drawer-pricing-philosophy'));

      this.ev.register(this.el.querySelector('.honest-pricing-trigger'), 'click', (e) => {
        e.preventDefault();
        this.pricingPhilosophyDrawerHp.open();
      });
    }

    const pswpJsonElement = this.el.querySelector('[data-photoswipe-feature-images]');
    if (pswpJsonElement) {
      const pswpJson = JSON.parse(pswpJsonElement.innerHTML);

      this.imageZoom = new ImageZoom(pswpJson.images);

      this.el.querySelectorAll('[data-image-zoom]').forEach(image => {
        this.ev.register(image, 'click', e => this.imageZoom.open(e.currentTarget.dataset.imageZoom, e.currentTarget));
      })
    }

    // Gallery scroll functionality (desktop only)
    this.el.querySelectorAll('.product-feature-gallery .gallery-scroll-container').forEach(container => {
      const scrollTrack = container.querySelector('[data-gallery-scroll]');
      const leftButton = container.querySelector('.gallery-scroll-button-left');
      const rightButton = container.querySelector('.gallery-scroll-button-right');
      
      if (!scrollTrack || !leftButton || !rightButton) return;
      
      // Only enable on desktop (720px+)
      if (window.innerWidth < 720) return;
      
      // Check if this is a reviews gallery (shows 6 images at a time)
      const isReviewsGallery = container.closest('.product-feature-gallery-reviews') !== null;
      const imagesPerView = isReviewsGallery ? 6 : 4;
      
      // Count image blocks to determine if scrolling is needed
      const imageBlocks = scrollTrack.querySelectorAll('.feature-block');
      const hasMoreThanView = imageBlocks.length > imagesPerView;
      
      const updateArrowStates = () => {
        const scrollLeft = scrollTrack.scrollLeft;
        const scrollWidth = scrollTrack.scrollWidth;
        const clientWidth = scrollTrack.clientWidth;
        const maxScroll = scrollWidth - clientWidth;
        
        // If we have more than the viewable amount, enable scrolling
        if (hasMoreThanView) {
          // Left arrow: disabled when at start (scrollLeft < 1)
          leftButton.disabled = scrollLeft < 1;
          
          // Right arrow logic:
          // Since images 5+ may be lazy loaded, scrollWidth might initially be inaccurate.
          // We use the count-based check as primary, then refine with scrollWidth when accurate.
          if (maxScroll > 0 && maxScroll > clientWidth * 0.5) {
            // scrollWidth appears accurate (there's substantial scrollable content)
            // Use precise scrollWidth-based check
            rightButton.disabled = scrollLeft >= maxScroll - 1;
          } else {
            // scrollWidth might not be accurate yet (lazy images not loaded) OR
            // we're near the end - use position-based heuristics
            // If we're at the start (scrollLeft < 10), definitely enable right arrow
            // If we've scrolled a bit, check if we're close to the end
            if (scrollLeft < 10) {
              // At the start, enable right arrow since we know there are more images
              rightButton.disabled = false;
            } else if (maxScroll > 0) {
              // Some content loaded, use scrollWidth check
              rightButton.disabled = scrollLeft >= maxScroll - 1;
            } else {
              // No accurate scrollWidth yet, but we know there are more images
              // Enable if we haven't scrolled far
              rightButton.disabled = scrollLeft >= clientWidth * 0.8;
            }
          }
        } else {
          // Fewer images than viewable, no scrolling needed
          leftButton.disabled = true;
          rightButton.disabled = true;
        }
      };
      
      // Scroll handlers
      this.ev.register(leftButton, 'click', () => {
        scrollTrack.scrollBy({ top: 0, left: -scrollTrack.clientWidth, behavior: 'smooth' });
      });
      
      this.ev.register(rightButton, 'click', () => {
        scrollTrack.scrollBy({ top: 0, left: scrollTrack.clientWidth, behavior: 'smooth' });
      });
      
      // Update arrow states on scroll
      let scrollTicking = false;
      this.ev.register(scrollTrack, 'scroll', () => {
        if (!scrollTicking) {
          window.requestAnimationFrame(() => {
            updateArrowStates();
            scrollTicking = false;
          });
          scrollTicking = true;
        }
      });
      
      // Listen for image load events to recalculate scroll state
      imageBlocks.forEach(block => {
        const images = block.querySelectorAll('img[data-rimg], img[data-rimg="lazy"]');
        images.forEach(img => {
          // Listen for rimg load events
          this.ev.register(img, 'rimg:load', () => {
            // Delay slightly to allow layout to update
            setTimeout(updateArrowStates, 100);
          });
          this.ev.register(img, 'rimg:loaded', () => {
            setTimeout(updateArrowStates, 100);
          });
          // Also listen for standard load events as fallback
          if (img.complete) {
            setTimeout(updateArrowStates, 100);
          } else {
            this.ev.register(img, 'load', () => {
              setTimeout(updateArrowStates, 100);
            });
          }
        });
      });
      
      // Use ResizeObserver to detect when scrollWidth changes (e.g., when images load)
      if (window.ResizeObserver) {
        const resizeObserver = new ResizeObserver(() => {
          updateArrowStates();
        });
        resizeObserver.observe(scrollTrack);
      }
      
      // Initial state - check after a short delay to allow initial images to load
      setTimeout(updateArrowStates, 100);
      // Also check after a longer delay for lazy-loaded images
      setTimeout(updateArrowStates, 1000);
    });

    const reviewsDrawer = document.getElementById('drawer-product-reviews');

    if (reviewsDrawer) {
      this.productReviewsDrawer = new Drawer(reviewsDrawer);

      this.ev.register(this.el.querySelector('.product-reviews-slider-cta a'), 'click', (e) => {
        e.preventDefault();
        this.productReviewsDrawer.open();
      });
    }

    if (this.el.querySelector('.product-feature-product_details_buttons')) {
      this.ev.register(this.el.querySelector('.product-bubbles-wrapper'), 'click', (e) => {
        e.preventDefault();
        this.el.querySelector('.product-feature-product_details').classList.add('product-feature-expanded');
        e.currentTarget.classList.toggle('expanded');

        e.currentTarget.parentElement.querySelectorAll('.expanded').forEach(expanded => {
          expanded.classList.remove('expanded');
        })

        const pdpClick = this.ev.register(document, 'click', (e) => {
          if (!e.target.closest('.product-bubbles-wrapper')) {
            this.el.querySelector('.product-bubbles-wrapper.expanded').classList.remove('expanded');
            this.ev.unregister(pdpClick);
          }
        });
      });
    }

    if (this.el.querySelector('.product-feature-slider')) {
      this.el.querySelectorAll('.product-slideshow').forEach((slideshow) => {
        const flkty = new Slider(slideshow, {
          wrapAround: true,
          prevNextButtons: true,
          pageDots: false,
          groupCells: true
        });

        this.productFeatureSlider.push(flkty);
      });
    }

    this.el.querySelectorAll('.product-feature-columns .grid-items.slider').forEach(el => {
      this.productFeatureSlider.push(new Slider(el, {
        wrapAround: true,
        prevNextButtons: true,
        pageDots: false,
        groupCells: true,
      }));
    });

    if (this.el.querySelector('.yotpo.product-reviews-slider')) {
      this.productReviewsSlider = new Slider(this.el.querySelector('.yotpo.product-reviews-slider'), {
        wrapAround: true,
        pauseAutoPlayOnHover: false,
        pageDots: false,
        groupCells: true,
      });
    }

    if (this.el.querySelector('.product-feature-product_details')) {
      const productDetailsTriggers = this.el.querySelectorAll('.product-details-trigger');
      const productDetailsTriggerWrappers = this.el.querySelectorAll('.product-details-trigger-wrapper');
      const productDetailsCounter = this.el.querySelector('.product-details-counter');

      function triggerState(index) {
        productDetailsTriggers.forEach(el => el.classList.remove('selected'));
        productDetailsTriggerWrappers.forEach(el => el.classList.remove('selected'));
        productDetailsTriggers[index].classList.add('selected');
        productDetailsTriggerWrappers[index].classList.add('selected');
        productDetailsTriggers.forEach(el => el.classList.add('reset-animation'));
        setTimeout(() => { productDetailsTriggers.forEach(el => el.classList.remove('reset-animation')); }, 150);
        if (productDetailsCounter) {
          productDetailsCounter.classList.add('animate');
          setTimeout(() => { productDetailsCounter.innerText = (index + 1); }, 150);
          setTimeout(() => { productDetailsCounter.classList.remove('animate'); }, 300);
        }
      }

      productDetailsTriggers.forEach(el => {
        this.ev.register(el, 'click', (e) => {
          e.preventDefault();
          if (e.currentTarget.classList.contains('selected')) {
            this.productDetailsSlider.select(productDetailsTriggers.length);
          } else if (e.currentTarget.dataset && e.currentTarget.dataset.counter) {
            this.productDetailsSlider.select(e.currentTarget.dataset.counter);
          }
        });
      });

      let initialIndex = document.querySelectorAll('.product-details-slider .product-details-slide').length - 1;

      this.el.querySelector('.product-details-slider').insertAdjacentHTML('beforeend', '<div class="product-details-slide info-slide"><p><i>Tap a \'+\' above to learn more.</i></p></div>');

      const productDetailsSlider = this.el.querySelector('.product-details-slider');

      this.productDetailsSlider = new Slider(productDetailsSlider, {
        wrapAround: true,
        // setGallerySize: false,
        prevNextButtons: false,
        pageDots: false,
        initialIndex: initialIndex,
        on: {
          select: function (index) {
            triggerState(index);
          }
        }
      });

      /* this.productDetailsSlider = new Slider ('.product-details-slider-nav', {
        // wrapAround: true,
        groupCells: true,
        // setGallerySize: false,
        // freeScroll: true,
        contain: true,
        prevNextButtons: false,
        pageDots: false,
        asNavFor: '.product-details-slider',
        draggable: false
      }); */
    }

    this.yotpo();

    this.heroSlider = this.heroSlider.bind(this);

    this.el.querySelectorAll('.product-feature-text_over_image_slider').forEach(this.heroSlider);

    this.featureBlocks = this.el.querySelectorAll('.product-feature');

    this.trackImpressions = this.trackImpressions.bind(this);
    this.trackImpressions();

    this.initAnagram();
  }

  initAnagram() {
    // Find all Anagram blocks that haven't been initialized
    this.el.querySelectorAll('[data-anagram-block-id]:not([data-anagram-initialized])').forEach(anagramRoot => {
      const blockId = anagramRoot.dataset.anagramBlockId;
      const productId = anagramRoot.dataset.anagramProductId;
      const productTitle = anagramRoot.dataset.anagramProductTitle;
      const pageHandle = anagramRoot.dataset.anagramPageHandle || 'product';
      
      // Mark as initialized to prevent duplicate initialization
      anagramRoot.dataset.anagramInitialized = 'true';

      // Add CSS link if not already present
      if (!document.querySelector('link[href="https://unpkg.com/@anagram-ai/engagement/index.css"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.type = 'text/css';
        link.href = 'https://unpkg.com/@anagram-ai/engagement/index.css';
        document.head.appendChild(link);
      }

      // Create and execute the module script
      const script = document.createElement('script');
      script.type = 'module';
      script.crossOrigin = 'anonymous';
      script.textContent = `
        import { renderAnagram } from "https://unpkg.com/@anagram-ai/engagement/index.js"
        const elementToAttachTo = document.querySelector("#anagram-root-${blockId}");
        if (elementToAttachTo) {
          renderAnagram(
            "a92a9b8f-eb36-4aab-ade1-8efb6f5b753f",
            elementToAttachTo,
            {
              productTitle: ${JSON.stringify(productTitle)},
              originOfInquiry: ${JSON.stringify(productTitle || pageHandle)},
              sourceProductId: ${JSON.stringify(String(productId))},
              pageHandle: ${JSON.stringify(pageHandle)},
              styleOptions: {
                "root": {
                  "width": "100%",
                  "height": "100%",
                  "paddingTop": "0px",
                  "paddingRight": "0px",
                  "paddingBottom": "0px",
                  "paddingLeft": "0px",
                  "backgroundColor": "rgba(0, 0, 0, 0)",
                  "mobile": {
                    "paddingTop": "0px",
                    "paddingRight": "0px",
                    "paddingBottom": "0px",
                    "paddingLeft": "0px"
                  }
                },
                "oneClickQuestions": {
                  "minHeight": "36px",
                  "backgroundColor": "rgba(0, 0, 0, 0.05)",
                  "borderWidth": "0px",
                  "borderColor": "rgba(0, 0, 0, 0)",
                  "borderRadius": "0px",
                  "fontFamily": "Nunito Sans, sans-serif",
                  "fontSize": "14px",
                  "lineHeight": "1.25",
                  "fontWeight": "500",
                  "fontColor": "rgba(107, 114, 128, 1)",
                  "iconSize": "20px",
                  "paddingTop": "10px",
                  "paddingBottom": "10px",
                  "paddingLeft": "12px",
                  "paddingRight": "12px",
                  "hoverStyles": {
                    "backgroundColor": "rgba(0, 0, 0, 0.1)",
                    "color": "rgba(75, 85, 99, 1)"
                  }
                },
                "launcher": {
                  "minHeight": "45px",
                  "borderColor": "rgba(156, 163, 175, 0.9)",
                  "borderWidth": "1px",
                  "borderRadius": "0px",
                  "input": {
                    "placeholderFontColor": "rgba(100, 116, 139, 1)",
                    "placeholderText": "Ask a question...",
                    "backgroundColor": "rgba(0, 0, 0, 0)",
                    "fontFamily": "Nunito Sans, sans-serif",
                    "fontSize": "15px",
                    "lineHeight": "1.25",
                    "fontWeight": "400",
                    "fontColor": "rgba(0, 0, 0, 0.75)",
                    "maxWidth": "100%",
                    "paddingTop": "12px",
                    "paddingBottom": "12px",
                    "paddingLeft": "12px",
                    "paddingRight": "12px"
                  },
                  "button": {
                    "text": "ASK AI",
                    "backgroundColor": "#205f83",
                    "fontFamily": "Nunito Sans, sans-serif",
                    "fontSize": "15px",
                    "lineHeight": "1.50",
                    "fontWeight": "600",
                    "fontColor": "rgba(248, 250, 252, 1)",
                    "iconSize": "22px",
                    "paddingTop": "8px",
                    "paddingBottom": "8px",
                    "paddingLeft": "16px",
                    "paddingRight": "16px",
                    "hoverStyles": {
                      "backgroundColor": "rgba(18, 18, 18, 0.90)"
                    }
                  },
                  "maxWidth": "100%",
                  "marginTop": "0px",
                  "marginRight": "0px",
                  "marginBottom": "0px",
                  "marginLeft": "0px",
                  "mobile": {
                    "maxWidth": "100%",
                    "marginTop": "0px",
                    "marginRight": "0px",
                    "marginBottom": "0px",
                    "marginLeft": "0px"
                  }
                },
                "dialog": {
                  "root": {
                    "borderRadius": "0px",
                    "mobile": {
                      "offsetBottom": "0px"
                    }
                  },
                  "userMessage": {
                    "iconSize": "32px",
                    "fontFamily": "Nunito Sans, sans-serif",
                    "fontSize": "22px",
                    "lineHeight": "1.50",
                    "fontWeight": "600",
                    "fontColor": "rgba(0, 0, 0, 0.9)"
                  },
                  "responseMessage": {
                    "fontFamily": "Nunito Sans, sans-serif",
                    "fontSize": "16px",
                    "lineHeight": "1.625",
                    "fontWeight": "400",
                    "fontColor": "rgba(0, 0, 0, 0.9)"
                  },
                  "launcher": {
                    "minHeight": "47px",
                    "borderColor": "rgba(156, 163, 175, 0.9)",
                    "borderWidth": "1px",
                    "borderRadius": "0px",
                    "input": {
                      "placeholderFontColor": "rgba(100, 116, 139, 1)",
                      "placeholderText": "Ask a question...",
                      "backgroundColor": "rgba(0, 0, 0, 0)",
                      "fontFamily": "Nunito Sans, sans-serif",
                      "fontSize": "16px",
                      "lineHeight": "1.25",
                      "fontWeight": "400",
                      "fontColor": "rgba(0, 0, 0, 0.75)",
                      "maxWidth": "100%",
                      "paddingTop": "12px",
                      "paddingBottom": "12px",
                      "paddingLeft": "12px",
                      "paddingRight": "12px"
                    },
                    "button": {
                      "text": "ASK AI",
                      "backgroundColor": "#205f83",
                      "fontFamily": "Nunito Sans, sans-serif",
                      "fontSize": "16px",
                      "lineHeight": "1.50",
                      "fontWeight": "600",
                      "fontColor": "rgba(248, 250, 252, 1)",
                      "iconSize": "22px",
                      "paddingTop": "8px",
                      "paddingBottom": "8px",
                      "paddingLeft": "16px",
                      "paddingRight": "16px",
                      "hoverStyles": {
                        "backgroundColor": "rgba(18, 18, 18, 0.90)"
                      }
                    }
                  }
                }
              }
            }
          );
        }
      `;
      document.head.appendChild(script);
    });
  }

  trackImpressions() {
    const featureBlockIo = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const featureBlockId = entry.target.id;
          heap.track('ProductSectionImpression', { sectionName: featureBlockId });
          featureBlockIo.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '-75px 0px -75px 0px',
      threshold: 0
    });

    this.featureBlocks.forEach((featureBlock) => {
      featureBlockIo.observe(featureBlock);
    });

    this.observers.push(featureBlockIo);
  }

  yotpo() {
    if (!this.yotpoEl) return;
    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting || entry.intersectionRatio > 0) {
          io.unobserve(entry.target);
          //Yotpo already loaded
          if (typeof window.yotpoWidgetsContainer === 'object') {
            window.yotpoWidgetsContainer.initWidgets();
          } else {
            loadjs(this.yotpoEl.dataset.yotpoUrl);
          }
        }
      });
    }, {
      rootMargin: '20% 0px'
    });

    io.observe(this.yotpoEl);
    this.observers.push(io);
  }

  heroSlider(el) {
    el.querySelectorAll('[data-toggle-hero]').forEach(toggle => {
      this.ev.register(toggle, 'click', (e) => {
        const showHero = el.querySelector(`[data-hero="${e.currentTarget.dataset.toggleHero}"]`);
        const mobileShowHero = el.querySelector(`[data-mobile-hero="${e.currentTarget.dataset.toggleHero}"]`);
        if (showHero.classList.contains('shown')) return;
        const shownHero = el.querySelector(`[data-hero].shown`);
        const mobileShownHero = el.querySelector(`[data-mobile-hero].shown`);
        const endEv = this.ev.register(showHero, 'transitionend', (e) => {
          shownHero.classList.remove('shown');
          mobileShownHero.classList.remove('shown');
          showHero.classList.remove('transitioning');
          mobileShowHero.classList.remove('transitioning');
          this.ev.unregister(endEv);
        });
        el.querySelectorAll('[data-toggle-hero].shown').forEach(shownToggle => {
          shownToggle.classList.remove('shown');
        })
        toggle.classList.add('shown');
        showHero.classList.add('will-show');
        showHero.classList.add('transitioning');
        showHero.scrollTop;
        showHero.classList.remove('will-show');
        showHero.classList.add('shown');
        mobileShowHero.classList.add('will-show');
        mobileShowHero.classList.add('transitioning');
        mobileShowHero.scrollTop;
        mobileShowHero.classList.remove('will-show');
        mobileShowHero.classList.add('shown');
      })
    })
  }

  onSectionUnload() {
    this.ev.unregisterAll();
    if (this.productReviewsDrawer) {
      this.productReviewsDrawer.unload();
    }
    if (this.imageZoom) {
      this.imageZoom.unload();
    }
    if (this.pricingPhilosophyDrawer) {
      this.pricingPhilosophyDrawer.unload();
    }
    this.observers.forEach(io => io.disconnect());
    if (this.productDetailsSlider) {
      this.productDetailsSlider.destroy();
    }
    if (this.productReviewsSlider) {
      this.productReviewsSlider.destroy();
    }
    this.productFeatureSlider.forEach(slider => slider.destroy());
  }
}
